const window = require('global/window')
import { ArcReviewDto, bsConstants, ellipsis as commonEllipsis } from 'booksprout'

export interface ClaimScrollRestorePayload {
  claimId?: number
  topOffset?: number
}

export const popupCenter = (args: { url: string, title: string, w: number, h: number }) => {
  const left = (window.outerWidth / 2) - (args.w / 2)
  const top = (window.outerHeight / 2) - (args.h / 2)
  const newWindow = window.open(args.url, args.title,
    `
      popup=true,
      width=${args.w},
      height=${args.h},
      top=${top},
      left=${left},
      title=${args.title},
      noopener=false
      `
  )

  newWindow?.focus()
  return newWindow
}

/**
 * Running inside the Capacitor shell rather than a browser.
 *
 * Both spellings are checked because they come from different Capacitor majors: `isNativePlatform()`
 * is the current one, `isNative` the legacy property. Lifted out of `IsReviewerApp` so callers that
 * need the platform itself - rather than which app they are - stop re-deriving it. [sc-28415]
 */
export const isNativeApp = (): boolean => !!(window.Capacitor && (
  (typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform()) ||
  window.Capacitor.isNative
))

// checking pathname.startsWith() instead indexOf()
// to avoid getting true on urls like "/publisher/settings/reviewer"
export const IsReviewerApp = () => {
  let isReviewerApp = !!process.env.SERVER

  if (
    (window.location && window.location.pathname.startsWith('/reviewer')) ||
    isNativeApp()
  ) {
    isReviewerApp = true
  }

  return isReviewerApp
}

export const ellipsis = (text: string, length: number, ellipsisStr: string = '...', cb: any = void 0) => {
  return commonEllipsis(text, length, ellipsisStr, cb)
}

export const buildClaimScrollRestorePayload = (
  claimId: number | undefined,
  element?: HTMLElement | null
): ClaimScrollRestorePayload => {
  return {
    claimId,
    topOffset: element?.getBoundingClientRect().top || 0
  }
}

export const restoreClaimScrollPosition = (
  payload?: ClaimScrollRestorePayload,
  attempts = 12
): void => {
  if (!payload?.claimId) {
    return
  }

  window.setTimeout(() => {
    const claimElement = document.querySelector(`[data-claim-id="${payload.claimId}"]`) as HTMLElement | null
    if (claimElement) {
      const targetTopOffset = payload.topOffset || 0
      window.scrollBy({ top: claimElement.getBoundingClientRect().top - targetTopOffset, behavior: 'auto' })
      return
    }

    if (attempts > 1) {
      restoreClaimScrollPosition(payload, attempts - 1)
    }
  }, 50)
}

export const getTranslatedReviewText = (review?: ArcReviewDto | null): string => {
  return review?.translatedReview?.trim() || ''
}

export const isEnglishReview = (review?: ArcReviewDto | null): boolean => {
  return (review?.reviewLanguage || '').toLowerCase().startsWith('en')
}

export const isUndetectableReview = (review?: ArcReviewDto | null): boolean => {
  return (review?.reviewLanguage || '').toLowerCase() === 'und'
}

export const hasTranslatedReview = (review?: ArcReviewDto | null): boolean => {
  return !!getTranslatedReviewText(review) && !isEnglishReview(review)
}

export const canTranslateReview = (review?: ArcReviewDto | null): boolean => {
  return !!review?.id
    && !hasTranslatedReview(review)
    && !isEnglishReview(review)
    && !isUndetectableReview(review)
    && new Date(review.createdDate).getTime() >= bsConstants.APP.NEW_BOOK_FIELDS_LIVE_DATE.getTime()
}
